import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/SurveyViewer.css";
import FullscreenLoader from "./FullscreenLoader";
import { fetchSurveyAccessToken } from "../helpers/apiHelper";
import { clearSurveyActive, markSurveyActive } from "../helpers/surveyActivity";
import { getModuleContext } from "../modules/moduleUtils";
const SURVEY_MESSAGE_VERSION = 1;
const DEFAULT_INVITATION_ID = "378bf3c4-2d7f-4105-86d0-ffea74f57c47";
const DEFAULT_SURVEY_URL =
  "https://pgsurvey-test.pogustgoodhead.com/invite/378bf3c4-2d7f-4105-86d0-ffea74f57c47";
export default function PortalSurveyViewer({
  module,
  notification,
  onClose,
  closing = false,
}) {
  const iframeRef = useRef(null);
  const activeModule = module || notification || null;
  const context = getModuleContext(activeModule);
  const [loaded, setLoaded] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [loadingToken, setLoadingToken] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [surveyToken, setSurveyToken] = useState(null);
  const invitationId = context?.invitationId || DEFAULT_INVITATION_ID;
  const baseSurveyUrl = context?.link || context?.invitationLink || DEFAULT_SURVEY_URL;
  const matterId = context?.matterId || null;
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    "";
  const surveyUrl = useMemo(() => {
    if (!baseSurveyUrl) return "";
    const separator = baseSurveyUrl.includes("?") ? "&" : "?";
    return `${baseSurveyUrl}${separator}embedded=portal&lang=pt`;
  }, [baseSurveyUrl]);
  const surveyOrigin = useMemo(() => {
    try {
      return new URL(baseSurveyUrl).origin;
    } catch {
      return "";
    }
  }, [baseSurveyUrl]);
  useEffect(() => {
    const timer = setTimeout(() => setShowClose(true), 1000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    markSurveyActive();
    return () => {
      clearSurveyActive();
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const loadToken = async () => {
      if (!token || !invitationId) {
        setTokenError("Missing required data for survey.");
        setLoadingToken(false);
        return;
      }
      try {
        const response = await fetchSurveyAccessToken(
          token,
          invitationId,
          matterId,
        );
        if (cancelled) return;
        if (!response?.accessToken) {
          throw new Error("Missing survey access token");
        }
        setSurveyToken(response);
      } catch (err) {
        if (cancelled) return;
        setTokenError(err.message || "Failed to load survey token");
      } finally {
        if (!cancelled) setLoadingToken(false);
      }
    };
    loadToken();
    return () => {
      cancelled = true;
    };
  }, [token, invitationId, matterId]);
  useEffect(() => {
    if (!surveyOrigin || !surveyToken) return;
    const postToSurvey = (payload) => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;
      iframeWindow.postMessage(payload, surveyOrigin);
    };
    const handleMessage = (event) => {
      if (event.origin !== surveyOrigin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      const correlationId = data?.correlationId;
      if (!correlationId) return;
      if (
        data.type === "pgs-survey.request-survey-access-token" ||
        data.type === "pgs-survey.refresh-survey-access-token"
      ) {
        postToSurvey({
          type: "pgs-survey.survey-access-token",
          version: SURVEY_MESSAGE_VERSION,
          correlationId,
          invitationId,
          accessToken: surveyToken.accessToken,
          expiresAt: surveyToken.expiresAt || null,
        });
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [surveyOrigin, surveyToken, invitationId]);
  const handleIframeLoad = () => {
    setLoaded(true);
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow || !surveyOrigin) return;
    setTimeout(() => {
      iframeWindow.postMessage(
        {
          type: "pgs-survey.host-ready",
          version: SURVEY_MESSAGE_VERSION,
        },
        surveyOrigin,
      );
    }, 300);
  };
  const handleClose = () => {
    if (closing) return;
    if (typeof onClose === "function") {
      onClose();
    }
  };
  return (
    <div className="surveyFullscreen">
      <button
        type="button"
        className={`surveyCloseTab ${showClose ? "visible" : ""}`}
        onClick={handleClose}
        disabled={closing}
      >
        {closing ? "Fechando..." : "Fechar"}
      </button>
      {closing ? (
        <FullscreenLoader
          title="Atualizando seu questionário"
          message="Estamos verificando o status mais recente."
        />
      ) : loadingToken ? (
        <FullscreenLoader
          title="Aguarde enquanto preparamos seu formulário"
          message="Este processo pode levar alguns minutos."
        />
      ) : tokenError ? (
        <div className="surveyEmptyState">
          Este formulário não está disponível.
        </div>
      ) : surveyUrl ? (
        <iframe
          ref={iframeRef}
          src={surveyUrl}
          title="Questionário"
          className={`surveyFullscreenFrame ${loaded ? "loaded" : ""}`}
          onLoad={handleIframeLoad}
        />
      ) : (
        <div className="surveyEmptyState">Questionário não encontrado.</div>
      )}
    </div>
  );
}
